import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Phone, Mail, MapPin, Facebook, Instagram, Clock } from "lucide-react";
const Footer = () => {
  const currentYear = new Date().getFullYear();
  const quickLinks = [{
    name: "Home",
    href: "/"
  }, {
    name: "About Us", 
    href: "/about"
  }, {
    name: "Contact",
    href: "/contact"
  }, {
    name: "FAQ",
    href: "/faq"
  }, {
    name: "Gift Cards",
    href: "/gift-card"
  }, {
    name: "Referral Program",
    href: "/referral"
  }, {
    name: "Service Comparison",
    href: "/services/comparison"
  }];
  const mainServices = [{
    name: "Deep Cleaning",
    href: "/services/deep-cleaning"
  }, {
    name: "Regular House Cleaning", 
    href: "/services/regular-cleaning"
  }, {
    name: "Moving Cleaning",
    href: "/services/moving-cleaning"
  }, {
    name: "Office Cleaning",
    href: "/services/office-cleaning"
  }, {
    name: "Window Cleaning",
    href: "/services/window-cleaning"
  }];

  const additionalServices = [{
    name: "Airbnb Cleaning",
    href: "/services/airbnb-cleaning"
  }, {
    name: "One-Time Cleaning",
    href: "/services/one-time-cleaning"
  }, {
    name: "Post-Construction",
    href: "/services/post-construction-cleaning"
  }, {
    name: "Spring Cleaning",
    href: "/services/spring-cleaning"
  }, {
    name: "Commercial Cleaning",
    href: "/services/commercial-cleaning"
  }];
  return <footer className="bg-primary text-primary-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {/* Company Info */}
            <div className="space-y-4">
              <img src="/assets/footer-logo.png" alt="Routine Cleaners Logo" width={85} height={48} loading="lazy" decoding="async" sizes="85px" className="h-12 w-auto" />
              <p className="text-primary-foreground/80 leading-relaxed">
                Connecticut's trusted cleaning service. Professional, reliable, 
                and committed to excellence in every clean.
              </p>
              <div className="space-y-3">
                <p className="text-sm font-semibold">Cleaning that fits your Routine</p>
                <div className="flex space-x-3">
                  <Button 
                    size="icon" 
                    className="text-brand-gold-light hover:bg-gradient-primary hover:text-white transition-all duration-300"
                    asChild
                  >
                    <a href="https://www.facebook.com/routinecleaners/" target="_blank" rel="noopener noreferrer" aria-label="Visit Routine Cleaners on Facebook">
                      <Facebook className="h-6 w-6" />
                    </a>
                  </Button>
                  <Button 
                    size="icon" 
                    className="text-brand-gold-light hover:bg-gradient-primary hover:text-white transition-all duration-300"
                    asChild
                  >
                    <a href="https://www.instagram.com/routinecleaners_/" target="_blank" rel="noopener noreferrer" aria-label="Follow Routine Cleaners on Instagram">
                      <Instagram className="h-6 w-6" />
                    </a>
                  </Button>
                  <Button 
                    size="icon" 
                    className="text-brand-gold-light hover:bg-gradient-primary hover:text-white transition-all duration-300"
                    asChild
                  >
                    <a href="https://share.google/BqqQqGbmzKXwSWQRp" target="_blank" rel="noopener noreferrer">
                      <span className="text-xl font-bold">G</span>
                    </a>
                  </Button>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-bold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                {quickLinks.map(link => <li key={link.name}>
                    <a href={link.href} className="text-primary-foreground/80 hover:text-brand-gold-light transition-colors">
                      {link.name}
                    </a>
                  </li>)}
              </ul>
            </div>

            {/* Services - Split into two columns */}
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-bold mb-4">Main Services</h3>
                <ul className="space-y-2">
                  {mainServices.map(service => <li key={service.name}>
                      <a href={service.href} className="text-primary-foreground/80 hover:text-brand-gold-light transition-colors">
                        {service.name}
                      </a>
                    </li>)}
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-4">Additional Services</h3>
                <ul className="space-y-2">
                  {additionalServices.map(service => <li key={service.name}>
                      <a href={service.href} className="text-primary-foreground/80 hover:text-brand-gold-light transition-colors">
                        {service.name}
                      </a>
                    </li>)}
                </ul>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-bold mb-4">Contact Us</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Phone className="h-4 w-4 text-brand-gold-light" />
                  <div className="text-sm">
                    <a href="tel:+12033503604" className="hover:text-brand-gold-light transition-colors">(203) 350-3604</a>
                    <div className="text-primary-foreground/60">Customer Service</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="h-4 w-4 text-brand-gold-light" />
                  <div className="text-sm">
                    <a href="tel:+14752517156" className="hover:text-brand-gold-light transition-colors">(475) 251-7156</a>
                    <div className="text-primary-foreground/60">Sales</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="h-4 w-4 text-brand-gold-light" />
                  <div className="text-sm">
                    <a href="mailto:contact@routinecleaners.com" className="hover:text-brand-gold-light transition-colors">contact@routinecleaners.com</a>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="h-4 w-4 text-brand-gold-light" />
                  <div className="text-sm">
                    <div>Office Hours</div>
                    <div className="text-primary-foreground/60">Mon-Fri: 8AM-5PM</div>
                    
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin className="h-4 w-4 text-brand-gold-light" />
                  <div className="text-sm">
                    <div>Serving all of Connecticut</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Separator className="bg-primary-foreground/20" />

        {/* Bottom Footer */}
        <div className="py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-primary-foreground/80">
              © {currentYear} Routine Cleaners. All rights reserved.
            </div>
            
            <div className="flex space-x-6 text-sm">
              <a href="/privacy-policy" className="text-primary-foreground/80 hover:text-brand-gold-light transition-colors">
                Privacy Policy
              </a>
              <a href="/terms-and-conditions" className="text-primary-foreground/80 hover:text-brand-gold-light transition-colors">
                Terms & Conditions
              </a>
            </div>
          </div>

          {/* Service Area Keywords for SEO */}
          <div className="mt-4 text-xs text-primary-foreground/60 text-center">
            <p>
              Professional cleaning services in Hartford, New Haven, Stamford, Bridgeport, Waterbury, 
              Norwalk, Danbury, New Britain, West Hartford, Greenwich, Hamden, Meriden, Bristol, 
              Manchester, West Haven, Milford, Stratford, East Hartford, Middletown, and all Connecticut cities.
            </p>
          </div>
        </div>
      </div>
    </footer>;
};
export default Footer;